import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {ReactNode} from 'react';

import {assert} from 'Asserts';
import FieldActionButtons, {ExtraActionButtons} from 'metadata/FieldActionButtons';
import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ElementMouseEvent} from 'util/Types';

export interface BaseEditableFieldProps {
  editor: ReactNode;
  isBeingEdited: boolean;
  isCompleteEnabled: boolean;
  isWritable: boolean;
  isFullWidth?: boolean;
  viewer: ReactNode;
  /** Specifies the right-aligned component next to the editor within the field (defaults to action buttons) */
  editorSiblingComponent?: ReactNode;
  onCancel: () => void;
  onComplete: () => void;
  onEdit: (e: ElementMouseEvent) => void | Promise<void>;
  dataTestAttrs?: DataTestAttrs;
  hasFieldActionButtonsOuterMargin?: boolean;
  extraActionButtons?: ExtraActionButtons;
}

/**
 * Component for displaying a value that can either be edited or viewed.
 * Provides buttons for starting, completing, and canceling edits.
 */
export default function BaseEditableField(props: BaseEditableFieldProps): JSX.Element {
  if (props.editorSiblingComponent != null) {
    assert(!props.isWritable, 'isWritable cannot be true if editorSiblingComponent is provided');
  }

  const editorSiblingComponent =
    props.editorSiblingComponent ??
    (props.isWritable && (
      <FieldActionButtons
        cancel={props.onCancel}
        complete={props.onComplete}
        edit={props.onEdit}
        isCompleteEnabled={props.isCompleteEnabled}
        isEditing={props.isBeingEdited}
        hasOuterMargin={props.hasFieldActionButtonsOuterMargin}
        extraActionButtons={props.extraActionButtons}
      />
    ));
  return (
    <div
      className={classNames(props.isFullWidth && css(styles.fullWidth), css(styles.height), 'editableField', {
        'is-beingEdited': props.isBeingEdited,
      })}
      {...getTestProps(props.dataTestAttrs)}
    >
      {props.isBeingEdited ? props.editor : props.viewer}
      {editorSiblingComponent}
    </div>
  );
}
BaseEditableField.propTypes = {
  editor: PropTypes.node.isRequired,
  isBeingEdited: PropTypes.bool.isRequired,
  isCompleteEnabled: PropTypes.bool.isRequired,
  isWritable: PropTypes.bool.isRequired,
  isFullWidth: PropTypes.bool,
  viewer: PropTypes.node.isRequired,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  dataTestAttrs: DataTestAttrsType,
  hasFieldActionButtonsOuterMargin: PropTypes.bool,
};

const styles = StyleSheet.create({
  fullWidth: {
    width: '100%',
  },
  height: {
    minHeight: '24px',
  },
});
